import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LogOut, ExternalLink, Linkedin, Facebook, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { blogsApi } from '../api/blogs';
import AIStudio from '../components/AIStudio';
import { useToast } from '../hooks/use-toast';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setBlogs(await blogsApi.listAdmin()); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await blogsApi.remove(id);
    load();
  };

  const quickPublish = async (id, title) => {
    if (!window.confirm(`Approve & publish "${title}"?\nThis will make the post live on the site.`)) return;
    setPublishingId(id);
    try {
      await blogsApi.publish(id);
      toast({ title: 'Published', description: `"${title}" is now live.` });
      load();
    } catch (err) {
      toast({
        title: 'Publish failed',
        description: err?.response?.data?.detail || 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setPublishingId(null);
    }
  };

  // One-click share — opens LinkedIn / Facebook share dialog with the post
  // URL pre-filled. The user confirms with 1 click on the dialog.
  const publicOrigin = window.location.origin;
  const shareOn = (network, slug, title) => {
    const url = encodeURIComponent(`${publicOrigin}/blog/${slug}`);
    const text = encodeURIComponent(title || 'New post from EasyWorks');
    const dialogs = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
    };
    window.open(dialogs[network], '_blank', 'width=680,height=680,noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-1 select-none">
              <span className="font-extrabold text-xl tracking-tight text-[#F39200]">easy</span>
              <span className="font-extrabold text-xl tracking-tight text-neutral-900">works</span>
            </Link>
            <span className="text-xs uppercase tracking-wider text-neutral-400 border-l border-black/10 pl-3">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="text-xs text-right hidden sm:block">
              <div className="text-neutral-900 font-semibold">{user?.name || user?.email}</div>
              <div className="text-neutral-500">{user?.email}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 text-sm text-neutral-700 hover:text-red-600 border border-neutral-200 hover:border-red-300 rounded-lg px-3 h-9 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 lg:px-10 py-10">
        <AIStudio onDraftCreated={() => load()} />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Blog posts</h1>
            <p className="text-sm text-neutral-600 mt-1">Manage all blog content. Drafts stay private until published.</p>
          </div>
          <button
            onClick={() => navigate('/admin/blogs/new')}
            data-testid="new-blog-btn"
            className="inline-flex items-center gap-2 bg-[#F39200] hover:bg-[#E08400] text-white h-11 px-5 rounded-lg text-sm font-semibold transition-colors"
          >
            <Plus size={16} /> New post
          </button>
        </div>
        {loading ? (
          <div className="text-center py-16 text-neutral-500 text-sm">Loading…</div>
        ) : blogs.length === 0 ? (
          <div className="bg-white border border-dashed border-black/10 rounded-2xl p-12 text-center">
            <div className="text-lg font-semibold text-neutral-900">No posts yet</div>
            <p className="mt-2 text-sm text-neutral-600">Publish your first blog to start attracting SEO traffic.</p>
            <button onClick={() => navigate('/admin/blogs/new')} className="mt-5 inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white h-10 px-4 rounded-lg text-sm font-semibold">
              <Plus size={14} /> Create post
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[1fr_90px_110px_320px] text-xs uppercase tracking-wide text-neutral-500 font-semibold bg-neutral-50 px-5 py-3">
              <div>Title</div>
              <div>Status</div>
              <div>Updated</div>
              <div className="text-right">Actions</div>
            </div>
            {blogs.map((b) => (
              <div key={b.id} data-testid={`blog-row-${b.id}`} className="grid grid-cols-[1fr_90px_110px_320px] items-center px-5 py-4 border-t border-black/5 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold text-neutral-900 truncate">{b.title}</div>
                  <div className="text-xs text-neutral-500 truncate">/blog/{b.slug}</div>
                </div>
                <div>
                  <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-1 rounded-full ${b.status === 'published' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-100 text-neutral-600 border border-neutral-200'}`}>
                    {b.status}
                  </span>
                </div>
                <div className="text-xs text-neutral-500">{new Date(b.updated_at).toLocaleDateString()}</div>
                <div className="flex items-center gap-1.5 justify-end">
                  {b.status === 'draft' && (
                    <button
                      onClick={() => quickPublish(b.id, b.title)}
                      disabled={publishingId === b.id}
                      data-testid={`publish-blog-${b.id}`}
                      title="Approve & publish"
                      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                    >
                      <CheckCircle2 size={12} /> {publishingId === b.id ? 'Publishing…' : 'Approve & publish'}
                    </button>
                  )}
                  {b.status === 'published' && (
                    <>
                      <button
                        onClick={() => shareOn('linkedin', b.slug, b.title)}
                        data-testid={`share-linkedin-${b.id}`}
                        title="Share on LinkedIn"
                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] font-semibold text-[#0A66C2] border border-[#0A66C2]/30 hover:bg-[#0A66C2]/10 transition-colors"
                      >
                        <Linkedin size={12} /> LinkedIn
                      </button>
                      <button
                        onClick={() => shareOn('facebook', b.slug, b.title)}
                        data-testid={`share-facebook-${b.id}`}
                        title="Share on Facebook"
                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] font-semibold text-[#1877F2] border border-[#1877F2]/30 hover:bg-[#1877F2]/10 transition-colors"
                      >
                        <Facebook size={12} /> Facebook
                      </button>
                      <Link to={`/blog/${b.slug}`} target="_blank" title="View" className="w-8 h-8 rounded-lg text-neutral-600 hover:text-[#F39200] hover:bg-[#F39200]/10 flex items-center justify-center">
                        <ExternalLink size={14} />
                      </Link>
                    </>
                  )}
                  <Link
                    to={`/admin/blogs/${b.id}/edit`}
                    data-testid={`edit-blog-${b.id}`}
                    title="Edit post"
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] font-semibold text-neutral-800 border border-neutral-300 hover:border-[#F39200] hover:text-[#F39200] transition-colors"
                  >
                    <Edit size={12} /> Edit
                  </Link>
                  <button onClick={() => del(b.id, b.title)} data-testid={`delete-blog-${b.id}`} title="Delete" className="w-8 h-8 rounded-lg text-neutral-600 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
